import { HeroState } from '$src/js/states/HeroState.svelte';

interface LoadTask {
  name: string;
  weight: number;
  loaded: boolean;
}

export class LoaderService {
  private tasks: LoadTask[] = [];
  private totalWeight = 0;
  private loadedWeight = 0;
  private progressCallback: ((progress: number, complete: boolean) => void) | null = null;

  /**
   * Sets the progress callback function to be called when the loading progress changes.
   * @param cb The callback function to be called with the current progress and completion status.
   */
  readonly onProgress = (cb: (progress: number, complete: boolean) => void): void => {
    this.progressCallback = cb;
  };

  /**
   * Registers a new loading task with the specified name and weight.
   * @param name The name of the loading task.
   * @param weight The weight of the loading task.
   */
  readonly registerTask = (name: string, weight = 1): void => {
    this.tasks.push({ name, weight, loaded: false });
    this.totalWeight += weight;
    this.updateProgress();
  };

  /**
   * Completes a loading task with the specified name.
   * @param name The name of the loading task to complete.
   */
  readonly completeTask = (name: string): void => {
    const task = this.tasks.find((t) => t.name === name);
    if (!task || task.loaded) return;

    task.loaded = true;
    this.loadedWeight += task.weight;
    this.updateProgress();

    if (this.loadedWeight >= this.totalWeight) {
      HeroState.assetsReady = true;
    }
  };

  /**
   * Updates the loading progress and calls the progress callback if provided.
   */
  private readonly updateProgress = (): void => {
    const progress = this.totalWeight > 0 ? this.loadedWeight / this.totalWeight : 0;
    HeroState.loadProgress = progress;
    const complete = this.loadedWeight >= this.totalWeight && this.totalWeight > 0;
    this.progressCallback?.(progress, complete);
  };

  /**
   * Checks if all loading tasks are complete.
   * @returns True if all loading tasks are complete, false otherwise.
   */
  readonly isComplete = (): boolean => {
    return this.loadedWeight >= this.totalWeight && this.totalWeight > 0;
  };
}
